import { apiRequest } from '../client'
import type { Product } from '../types'

/*
 * Stock usage, tracking mode, unit display name and variants of a product
 * (CHG-2026-09-stock-usage-and-price-twins, tasks 9.1–9.3). All quantities are decimal JSON
 * numbers with at most 3 decimals, in the stock article's base unit.
 */

/** One stock article a dish uses per sold unit. */
export interface StockUsagePart {
  sku: string
  name: string
  qty: number
  /** The part's base unit as shown ("Stk" unless a display name is set). */
  unit: string
  stockTracked: boolean
  fractionalCounting: boolean
}

/**
 * A live dish that uses this article as a part, or (with `variantOf`) a variant of such a dish,
 * listed right after it; `qty` is what one sale books of this article (for a variant: the dish's
 * quantity × share, rounded to 3 decimals).
 */
export interface StockUsageUsedIn {
  sku: string
  name: string
  qty: number
  /** The dish SKU this entry is a variant of. */
  variantOf?: string
}

/** A variant's price at one restaurant. */
export interface VariantPrice {
  restaurantId: number
  restaurantName?: string
  amountCents: number
}

/** A live variant of this product. */
export interface ProductVariantSummary {
  sku: string
  name: string
  publicName?: string
  share: number
  active: boolean
  /** Inactive because its source is inactive; comes back with the source. */
  pausedBySource: boolean
  /** The variant's own prices; absent from a backend that predates phase B3. */
  prices?: Array<VariantPrice>
}

/** `GET /api/admin/products/{sku}/stock-usage`. */
export interface StockUsageView {
  sku: string
  name: string
  isDish: boolean
  isVariant: boolean
  stockTracked: boolean
  fractionalCounting: boolean
  /** The paydesk display name of the base unit, when one is set. */
  stockUnitLabel?: string
  /** The base unit as shown: the display name, else "Stk". */
  stockUnit: string
  /**
   * True while a usage row or variant uses the article fractionally: "Teilmengen zählen" cannot be
   * switched off then (409 fractional_usage_exists).
   */
  fractionalLocked?: boolean
  parts: Array<StockUsagePart>
  usedIn: Array<StockUsageUsedIn>
  variants: Array<ProductVariantSummary>
  /** Present on a variant: its source and share. */
  variantOf?: { sku: string; share: number }
}

/**
 * `PUT …/stock-usage`. A missing field is left as it is; `parts: []` removes the usage list
 * (the dish becomes a plain article at stock 0); `stockUnitLabel: ''` clears the display name.
 */
export interface StockUsageSaveRequest {
  tracked?: boolean
  fractionalCounting?: boolean
  stockUnitLabel?: string
  parts?: Array<{ sku: string; qty: number }>
  confirmCorrection?: boolean
}

/** One correction to 0 the save books (or, in a 409, would book). */
export interface StockCorrection {
  restaurantId: number
  restaurantName?: string
  availableQty: number
  movementId?: number
}

export type StockCorrectionReason = 'converted_to_dish' | 'tracking_turned_off'

/** Body of `409 stock_correction_required`. */
export interface StockCorrectionRequired {
  code: 'stock_correction_required'
  reason: StockCorrectionReason
  corrections: Array<StockCorrection>
}

export type StockUsageSaveResponse = StockUsageView & {
  corrections: Array<StockCorrection>
}

/** One reason the base unit may not change right now (task 4.5). */
export type BaseUnitGuardReason =
  | {
      type: 'stock'
      restaurantId: number
      restaurantName?: string
      availableQty: number
    }
  | { type: 'used_in_dish'; sku: string; name: string; qty: number }
  | { type: 'variant'; sku: string; name: string; share: number }
  | {
      type: 'open_delivery'
      deliveryId: string
      restaurantId: number
      restaurantName?: string
      expectedDate: string | null
      expectedQty: number
    }

export interface BaseUnitGuard {
  sku: string
  canChange: boolean
  reasons: Array<BaseUnitGuardReason>
}

/** One restaurant of `GET …/availability`. */
export interface ProductAvailabilityItem {
  restaurantId: number
  restaurantName?: string
  /** What the guest menu shows, in whole units; null when unlimited. */
  onlineQty: number | null
  /** What the store has, as the POS tile shows it; null when unlimited or without a stock row. */
  inStoreQty: number | null
  unlimited: boolean
  limitingSku: string | null
  limitingName: string | null
}

export interface ProductAvailability {
  sku: string
  items: Array<ProductAvailabilityItem>
}

export interface CreateVariantRequest {
  name: string
  publicName?: string
  /** Defaults to 1 on the server. */
  share?: number
  /** Needs `price:edit`; omit (or send empty) without it. */
  prices?: Array<{ restaurantId: number; amountCents: number }>
}

export interface CreateVariantResponse {
  ok: boolean
  variant: Product
  bundlesPublished: number
}

const base = (sku: string) =>
  `/api/v0/admin/products/${encodeURIComponent(sku)}`

export const productStockApi = {
  getStockUsage: (sku: string) =>
    apiRequest<StockUsageView>(`${base(sku)}/stock-usage`),

  /**
   * Saves the stock mode. A first usage list or tracking turned off while stock is on record is
   * refused with `409 stock_correction_required` (see {@link StockCorrectionRequired}) until the
   * request repeats with `confirmCorrection: true`.
   */
  saveStockUsage: (sku: string, body: StockUsageSaveRequest) =>
    apiRequest<StockUsageSaveResponse>(`${base(sku)}/stock-usage`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  /** The product's online and in-store availability at every restaurant, in one batch (`inventory:view`). */
  availability: (sku: string) =>
    apiRequest<ProductAvailability>(`${base(sku)}/availability`),

  baseUnitGuard: (sku: string) =>
    apiRequest<BaseUnitGuard>(`${base(sku)}/base-unit-guard`),

  createVariant: (sourceSku: string, body: CreateVariantRequest) =>
    apiRequest<CreateVariantResponse>(`${base(sourceSku)}/variants`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
}

/** Query key of a product's stock usage view; lives under the product's own key. */
export const stockUsageQueryKey = (sku: string) =>
  ['products', sku, 'stock-usage'] as const

/** Query key of a product's availability per restaurant. */
export const availabilityQueryKey = (sku: string) =>
  ['products', sku, 'availability'] as const

/** Query key of a product's base-unit guard. */
export const baseUnitGuardQueryKey = (sku: string) =>
  ['products', sku, 'base-unit-guard'] as const
